/**
 * Behaviour for blocks/subscription-addon.liquid.
 *
 * Reads its whole catalog off the block's data- attributes, so the prices and
 * variant ids stay in the Liquid where storefront/pricing.test.ts can parse
 * them, and this file never needs editing when a price moves.
 *
 * Loaded with `defer`, which runs it after the document parses and well before
 * any click. The add-to-cart intercept binds on document in the CAPTURE phase,
 * so it still runs ahead of the theme's own element-level handler regardless
 * of which script registered first.
 */
(function () {
  var root = document.querySelector('.ruck-addons[data-ruck-mode]');
  if (!root) return;

  var MODE = root.dataset.ruckMode; // 'device' | 'plan' | 'component'

  // Two products are reachable but no longer for sale, and the block points
  // each at the page that does sell the thing.
  //
  // The retired Additional Person kept a page anyone can reach (#1466 had it
  // published to the Online Store). Bought there on its own it is a $75
  // recurring charge against no plan, entitling nothing.
  // `extensions/plan-bound-addons` refuses that cart at checkout; this refuses
  // it a step earlier, where the buyer can still be told where to go. The
  // Anvil five-pack is retired for a gentler reason: the Anvil page sells any
  // quantity at the same price, so the pack was a duplicate.
  if (MODE === 'component') {
    var noticeHref = root.dataset.noticeHref || '/';
    document.addEventListener(
      'click',
      function (e) {
        if (!e.target.closest('.add-to-cart-button, add-to-cart-component button')) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        window.location.href = noticeHref;
      },
      true
    );
    return;
  }

  // Ruck Connect, one seat per person.
  var PLAN = { id: root.dataset.planVariant, seatCents: +root.dataset.seatPrice };
  // The retired Additional Person and five-person variants, cleared from an
  // old cart, never added.
  var RETIRED_IDS = (root.dataset.retiredVariants || '').split(',').filter(Boolean);
  // Each product's own selling plan, read off the product by the block (the
  // Subscriptions app's plan). Empty when a product carries none.
  var PLAN_SELLING_PLAN = +root.dataset.planSellingPlan || 0;
  var WEB = { id: root.dataset.webVariant, cents: +root.dataset.webPrice };
  var WEB_SELLING_PLAN = +root.dataset.webSellingPlan || 0;
  // From this many people there is no checkout: the buyer asks for a quote.
  var SALES_MIN_PEOPLE = +root.dataset.salesMinPeople || 5;
  var SALES_HREF = root.dataset.salesHref || '';
  // Where the on-page form posts (convex/billing/salesRequestHttp.ts). With
  // none, Contact sales falls back to the web app's form.
  var SALES_ENDPOINT = root.dataset.salesEndpoint || '';
  var BUY_BUTTONS = '.add-to-cart-button, add-to-cart-component button';
  var MAX_PEOPLE = 200;
  var ANVIL_PACK = { id: root.dataset.anvilPackVariant, units: +root.dataset.anvilPackUnits };

  // On a plan page the headcount is its own stepper. On a device page there is
  // no second stepper at all: the headcount IS the Anvil quantity, read off the
  // theme's own selector. Two controls that can disagree about how many people
  // are being bought is the failure this block exists to remove, and six Anvils
  // beside a plan for one is nobody's intention.
  var peopleInput = document.getElementById('ruck-people');
  var deviceQtyInput = document.querySelector('product-form-component input[name="quantity"]')
    || document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
  var webToggle = document.getElementById('ruck-web-toggle');
  var planConnect = document.getElementById('ruck-plan-connect');
  var planFree = document.getElementById('ruck-plan-free');
  var errorEl = document.getElementById('ruck-addon-error');
  var totalEl = document.getElementById('ruck-quote-total');
  var detailEl = document.getElementById('ruck-quote-detail');
  var salesNoteEl = document.getElementById('ruck-sales-note');
  var salesLinkEl = document.getElementById('ruck-sales-link');
  var salesDialog = document.getElementById('ruck-sales-dialog');
  var salesForm = document.getElementById('ruck-sales-form');
  var salesErrorEl = document.getElementById('ruck-sales-error');
  var salesSentEl = document.getElementById('ruck-sales-sent');
  var salesSentToEl = document.getElementById('ruck-sales-sent-to');
  var salesSubmitEl = document.getElementById('ruck-sales-submit');

  // --- the rule -------------------------------------------------------
  // resolveOrderSeats(): one Ruck Connect seat per person, the same price at
  // every size, and no checkout from SALES_MIN_PEOPLE up. Keep this in step
  // with convex/billing/pricing.ts.
  function resolve(people) {
    var n = Math.min(MAX_PEOPLE, Math.max(1, Math.floor(people) || 1));
    var cents = n * PLAN.seatCents;
    return { people: n, cents: cents, sales: n >= SALES_MIN_PEOPLE };
  }

  /** The Contact sales form, told how many people the buyer asked about. */
  function salesHref(people) {
    if (!SALES_HREF) return '';
    return SALES_HREF + (SALES_HREF.indexOf('?') === -1 ? '?' : '&') + 'seats=' + people;
  }

  function money(cents) {
    return cents % 100 === 0
      ? '$' + cents / 100
      : '$' + (cents / 100).toFixed(2);
  }

  function planChosen() {
    return MODE === 'plan' || (planConnect && planConnect.checked);
  }

  function currentPeople() {
    var source = MODE === 'device' ? deviceQtyInput : peopleInput;
    return source ? parseInt(source.value, 10) : 1;
  }

  // --- the buy button's words -----------------------------------------
  //
  // From SALES_MIN_PEOPLE up there is nothing to add to a cart, so the theme's
  // own buttons (the form's and the sticky bar's) say what they now do. Only
  // the label text node is swapped, never the button, so the theme's handlers
  // and its "added" animation markup stay as they were. The theme re-renders
  // the buy buttons on a variant change, which would put "Add to cart" back,
  // so a MutationObserver re-applies the label; a pass that finds nothing to
  // change changes nothing, which is what stops it feeding itself.
  var salesOn = false;
  var ADD_LABEL = /^add to cart$/i;
  var SALES_LABEL = 'Contact sales';

  function labelNodes(button) {
    var found = [];
    var walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    for (var node = walker.nextNode(); node; node = walker.nextNode()) {
      var text = node.nodeValue.trim();
      if (ADD_LABEL.test(text) || text === SALES_LABEL) found.push(node);
    }
    return found;
  }

  function applyButtonLabels() {
    document.querySelectorAll(BUY_BUTTONS).forEach(function (button) {
      labelNodes(button).forEach(function (node) {
        if (node.ruckOriginal === undefined) node.ruckOriginal = node.nodeValue;
        var wanted = salesOn ? SALES_LABEL : node.ruckOriginal;
        if (node.nodeValue.trim() !== wanted.trim()) node.nodeValue = wanted;
      });
    });
  }

  var labelQueued = false;
  new MutationObserver(function () {
    if (labelQueued) return;
    labelQueued = true;
    requestAnimationFrame(function () {
      labelQueued = false;
      applyButtonLabels();
    });
  }).observe(document.body, { childList: true, subtree: true, characterData: true });

  function setSales(on) {
    salesOn = on;
    root.dataset.sales = on ? 'on' : 'off';
    document.body.classList.toggle('ruck-sales-on', on);
    applyButtonLabels();
  }

  // Express checkout (Shop Pay, Apple Pay) buys the page's own variant times
  // the theme's quantity, and nothing this block composes. On the device page
  // with Free chosen that is only the right order below a pack: six Anvils
  // through it are six singles, $1,074, where the cart this block builds is a
  // pack and a single, $929 (`anvilOrderLines`). So it goes wherever the block
  // composes the order: a plan is chosen, or the count reaches a pack.
  function expressWouldMisprice(planOn) {
    if (planOn) return true;
    if (MODE !== 'device' || !deviceQtyInput || !ANVIL_PACK.id || !(ANVIL_PACK.units > 0)) return false;
    return (parseInt(deviceQtyInput.value, 10) || 1) >= ANVIL_PACK.units;
  }

  function render() {
    var on = planChosen();
    root.dataset.plan = on ? 'on' : 'off';
    document.body.classList.toggle('ruck-plan-on', on);
    document.body.classList.toggle('ruck-express-off', expressWouldMisprice(on));
    if (salesNoteEl) salesNoteEl.hidden = true;
    if (!on) {
      setSales(false);
      return;
    }

    var q = resolve(currentPeople());
    // A team this big gets a quote instead of a price, and the buy button
    // opens the form that asks for one.
    setSales(q.sales);
    if (salesNoteEl) salesNoteEl.hidden = !q.sales;
    if (salesLinkEl) salesLinkEl.href = salesHref(q.people);
    if (q.sales) {
      totalEl.textContent = 'Custom quote for ' + q.people + ' people';
      detailEl.textContent = MODE === 'device' ? 'One person per Anvil' : '';
      return;
    }
    totalEl.textContent =
      money(q.cents) + '/mo for ' + q.people + (q.people === 1 ? ' person' : ' people');

    var breakdown = q.people === 1 ? '' : money(PLAN.seatCents) + ' per person';
    // Say WHERE the number came from, or a device page reads as though the
    // plan size were fixed at whatever the quote happens to show.
    detailEl.textContent =
      MODE === 'device'
        ? 'One person per Anvil' + (breakdown === '' ? '' : '. ' + breakdown)
        : breakdown;
  }

  function clampPeople() {
    if (!peopleInput) return;
    peopleInput.value = resolve(currentPeople()).people;
    render();
  }

  function stepPeople(delta) {
    if (!peopleInput) return;
    peopleInput.value = resolve(currentPeople() + delta).people;
    render();
  }

  // A link from the app says how many people it just priced (`?people=`,
  // STOREFRONT_PEOPLE_PARAM in convex/billing/shopifyCatalog.ts), so the
  // stepper starts at the team instead of at one. Otherwise a buyer who had
  // been quoted three people and never touched the stepper paid for one. Only
  // a plain whole number is read, and the buyer can still change it.
  function peopleFromLink() {
    var raw = null;
    try {
      raw = new URLSearchParams(window.location.search).get('people');
    } catch {
      raw = null;
    }
    if (raw === null || !/^[0-9]{1,3}$/.test(raw)) return null;
    var n = parseInt(raw, 10);
    return n >= 1 && n <= MAX_PEOPLE ? n : null;
  }

  if (peopleInput) {
    var linkedPeople = MODE === 'plan' ? peopleFromLink() : null;
    if (linkedPeople !== null) peopleInput.value = linkedPeople;
    peopleInput.addEventListener('input', render);
    peopleInput.addEventListener('blur', clampPeople);
  }
  if (deviceQtyInput) {
    // The theme's stepper sets `.value` directly and fires no native event, so
    // the buttons are only observable through its own bubbling custom event.
    // The native listeners still matter for someone typing into the field.
    document.addEventListener('quantity-selector:update', render);
    deviceQtyInput.addEventListener('input', render);
    deviceQtyInput.addEventListener('change', render);
  }
  var minus = document.getElementById('ruck-people-minus');
  var plus = document.getElementById('ruck-people-plus');
  if (minus) minus.addEventListener('click', function () { stepPeople(-1); });
  if (plus) plus.addEventListener('click', function () { stepPeople(1); });
  if (planFree) planFree.addEventListener('change', render);
  if (planConnect) planConnect.addEventListener('change', render);
  render();

  // Tap-to-toggle tooltips for touch devices; close others on outside click.
  document.querySelectorAll('.ruck-info-btn').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var wrap = btn.closest('.ruck-info-wrap');
      var wasActive = wrap.classList.contains('ruck-tooltip-active');
      document.querySelectorAll('.ruck-tooltip-active').forEach(function (w) {
        w.classList.remove('ruck-tooltip-active');
      });
      if (!wasActive) wrap.classList.add('ruck-tooltip-active');
    });
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.ruck-info-wrap')) {
      document.querySelectorAll('.ruck-tooltip-active').forEach(function (w) {
        w.classList.remove('ruck-tooltip-active');
      });
    }
  });

  /** The colour name for a variant id, from the map the block emitted. */
  function colourOfSelectedVariant(variantId) {
    var node = document.querySelector('script[data-ruck-anvil-variants]');
    if (!node) return null;
    try {
      var match = JSON.parse(node.textContent).find(function (v) {
        return String(v.id) === String(variantId);
      });
      return match ? match.title : null;
    } catch {
      return null;
    }
  }

  // --- contact sales --------------------------------------------------
  //
  // The request form, in a dialog on this page. It used to be a link to
  // app.ruck.com/contact-sales, which in the middle of buying looked like being
  // thrown out of the store. That page is still where the note's link goes
  // without script, or when the dialog cannot open.

  function salesField(name) {
    return salesForm ? salesForm.elements.namedItem(name) : null;
  }

  /** What the rep needs to know that the form does not ask: what was on the page. */
  function salesContext() {
    var parts = [];
    if (MODE === 'device' && deviceQtyInput) {
      var count = parseInt(deviceQtyInput.value, 10) || 1;
      var idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      var colour = idInput ? colourOfSelectedVariant(idInput.value) : null;
      parts.push(count + (count === 1 ? ' Anvil' : ' Anvils') + (colour ? ' (' + colour + ')' : ''));
    }
    if (webToggle && webToggle.checked) parts.push('Ruck Web');
    return parts.length === 0 ? '' : 'From the store: ' + parts.join(', ') + '.';
  }

  function showSalesError(message) {
    if (!salesErrorEl) return;
    salesErrorEl.textContent = message;
    salesErrorEl.hidden = message === '';
  }

  function openSalesForm(people) {
    var canOpen = salesDialog && salesForm && SALES_ENDPOINT && typeof salesDialog.showModal === 'function';
    if (!canOpen) {
      if (SALES_HREF) window.location.href = salesHref(people);
      return;
    }
    // A request already sent starts a fresh form; one half typed keeps what
    // was typed, except the team size, which follows the page.
    if (!salesSentEl.hidden) {
      salesForm.reset();
      salesSentEl.hidden = true;
      salesForm.hidden = false;
    }
    showSalesError('');
    salesField('teamSize').value = String(people);
    if (!salesDialog.open) salesDialog.showModal();
    var first = salesField('name');
    if (first && first.value === '') first.focus();
  }

  function closeSalesForm() {
    if (salesDialog && salesDialog.open) salesDialog.close();
  }

  var sending = false;

  function sendSalesRequest(e) {
    e.preventDefault();
    if (sending) return;
    var note = salesField('note').value.trim();
    var context = salesContext();
    var body = {
      name: salesField('name').value,
      email: salesField('email').value,
      company: salesField('company').value,
      teamSize: salesField('teamSize').value,
      note: [note, context].filter(Boolean).join('\n\n'),
    };
    sending = true;
    showSalesError('');
    salesSubmitEl.disabled = true;
    salesSubmitEl.textContent = 'Sending…';
    fetch(SALES_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(
        function (result) {
          if (result !== null && result.ok === true) {
            salesSentToEl.textContent = body.email.trim();
            salesForm.hidden = true;
            salesSentEl.hidden = false;
            var done = salesSentEl.querySelector('[data-ruck-sales-close]');
            if (done) done.focus();
            return;
          }
          // A refusal carries the server's own sentence ("Enter your name.").
          if (result !== null && result.ok === false && typeof result.message === 'string') {
            showSalesError(result.message);
            return;
          }
          fallBackToWebForm();
        },
        // The request never got an answer we can read: offline, or the route
        // is not there (a theme published ahead of the backend, whose 404 has
        // no CORS headers and so reaches us as a bare network error).
        fallBackToWebForm
      )
      .then(function () {
        sending = false;
        salesSubmitEl.disabled = false;
        salesSubmitEl.textContent = 'Send request';
      });

    // The web app's form takes the same request, so going there beats
    // stranding the buyer on an error they cannot fix.
    function fallBackToWebForm() {
      if (SALES_HREF) {
        window.location.href = salesHref(parseInt(body.teamSize, 10) || resolve(currentPeople()).people);
      } else {
        showSalesError('Could not send your request. Try again.');
      }
    }
  }

  if (salesForm) salesForm.addEventListener('submit', sendSalesRequest);
  if (salesDialog) {
    salesDialog.querySelectorAll('[data-ruck-sales-close]').forEach(function (btn) {
      btn.addEventListener('click', closeSalesForm);
    });
    // A click on the backdrop lands on the dialog itself, never on its content.
    salesDialog.addEventListener('click', function (e) {
      if (e.target === salesDialog) closeSalesForm();
    });
  }
  if (salesLinkEl) {
    salesLinkEl.addEventListener('click', function (e) {
      if (!SALES_ENDPOINT || !salesDialog) return;
      e.preventDefault();
      openSalesForm(resolve(currentPeople()).people);
    });
  }

  // --- add to cart ----------------------------------------------------
  //
  // Adding is a read, a remove, then an add, and none of it is atomic. Two
  // clicks in flight can interleave into remove, remove, add, add and leave
  // the buyer with two plans, so a second click is dropped until the first
  // finishes. A user double-clicking Add to cart is ordinary, not an edge
  // case.
  var busy = false;

  function showError(message) {
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function post(path, payload) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res.json().then(
        function (body) {
          if (res.ok) return body;
          // Shopify's Ajax errors carry the useful half in `description`.
          // The old generic alert hid exactly the message that would have
          // named the unpublished variant behind #1466.
          var e = new Error(body.description || body.message || 'Cart error ' + res.status);
          throw e;
        },
        function () {
          throw new Error('Cart error ' + res.status);
        }
      );
    });
  }

  // Zero every cart line of these variants, naming each line by its KEY.
  // Keying `/cart/update.js` by variant id is refused for a subscription-only
  // product ("Variant can only be purchased with a selling plan"), even at
  // quantity 0 on an empty cart, so every plan product needs this path once
  // it is sold only on subscription. A line key names the line as it is,
  // selling plan included.
  function clearLines(variantIds) {
    if (variantIds.length === 0) return Promise.resolve();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart error ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var updates = {};
        (cart.items || []).forEach(function (line) {
          if (variantIds.indexOf(String(line.variant_id)) !== -1) updates[line.key] = 0;
        });
        return Object.keys(updates).length > 0
          ? post('/cart/update.js', { updates: updates })
          : null;
      });
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest(BUY_BUTTONS);
      if (!btn) return;
      if (!document.querySelector('.ruck-addons[data-ruck-mode]')) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      if (busy) return;
      errorEl.hidden = true;

      var items = [];

      // The device page sells the thing the page is about. The plan pages do
      // not: the resolver composes the plan lines from the headcount, whatever
      // product page the buyer came in through.
      if (MODE === 'device') {
        // The sticky bar's Add to cart sits outside the product form, so
        // fall back to the page's own form rather than reporting a missing
        // colour the buyer has already chosen.
        var form =
          btn.closest('form') ||
          document.querySelector('form[action*="/cart/add"]') ||
          document.querySelector('product-form-component form');
        var idInput = form && form.querySelector('input[name="id"]');
        var qtyInput = form && form.querySelector('input[name="quantity"]');
        if (!idInput || !idInput.value) {
          showError('Choose a colour first.');
          return;
        }
        // Decompose exactly as `anvilOrderLines` does on the server: as many
        // packs as fit, then singles. Adding the count as singles instead
        // would charge $1,074 for six devices rather than $929.
        var wanted = (qtyInput && parseInt(qtyInput.value, 10)) || 1;
        var packs = ANVIL_PACK.id ? Math.floor(wanted / ANVIL_PACK.units) : 0;
        var singles = wanted - packs * ANVIL_PACK.units;
        if (packs > 0) {
          // The pack has no colour of its own, so the one the buyer picked
          // rides along as a line property. Without it the order says only
          // "5 Pack" and whoever packs the box has to guess.
          var colour = colourOfSelectedVariant(idInput.value);
          items.push({
            id: ANVIL_PACK.id,
            quantity: packs,
            properties: colour === null ? {} : { Color: colour },
          });
        }
        if (singles > 0) items.push({ id: idInput.value, quantity: singles });
      }

      // Variants this block owns and is about to rewrite. Zeroing them first
      // makes a second click replace the plan rather than stack another one.
      var owned = [];

      if (planChosen()) {
        var q = resolve(currentPeople());
        // Five or more never check out on their own: the button reads
        // Contact sales, and pressing it asks for the quote right here. A
        // device page still sells the Anvils alone once Free is chosen.
        if (q.sales) {
          openSalesForm(q.people);
          return;
        }
        // A subscription line with no selling plan is refused by the store,
        // and would bill once if it were not. Say so instead of trying.
        if (!PLAN_SELLING_PLAN) {
          showError('Ruck Connect cannot be added right now. Please email support@ruck.com.');
          return;
        }
        owned.push(PLAN.id);
        owned.push.apply(owned, RETIRED_IDS);
        items.push({ id: PLAN.id, quantity: q.people, selling_plan: PLAN_SELLING_PLAN });
      }

      if (webToggle && webToggle.checked) {
        if (!WEB_SELLING_PLAN) {
          showError('Ruck Web cannot be added right now. Please email support@ruck.com.');
          return;
        }
        owned.push(WEB.id);
        items.push({ id: WEB.id, quantity: 1, selling_plan: WEB_SELLING_PLAN });
      }

      if (items.length === 0) {
        showError('Choose a plan or a device first.');
        return;
      }

      var clear = clearLines(owned);

      busy = true;
      clear
        .then(function () {
          return post('/cart/add.js', { items: items });
        })
        .then(function () {
          // Stays busy on the way out: the navigation is already committed,
          // and clearing it here would let a click land on the old page and
          // add a second plan we would never see.
          window.location.href = '/cart';
        })
        .catch(function (err) {
          busy = false;
          console.error('[ruck] add to cart failed', err);
          showError(err.message + ' If it keeps happening, email support@ruck.com.');
        });
    },
    true
  );
})();
